import api from "@/api";
import deepClone from "@/utils/deep-clone";

const state = {
    list: [],
    details: {},
    lastCreatedId: null,
};

const getters = {
    list(state) {
        return deepClone(state.list ?? []);
    },
    sortedList(state) {
        return deepClone(sortCyclesByStatus(state?.list ?? []));
    },
    count(state) {
        return state?.list?.length ?? 0;
    },
    lastCreatedId(state) {
        return state.lastCreatedId;
    },
    details(state) {
        return id => deepClone(state.details[id]);
    },
};

const actions = {
    getList({ commit }) {
        return api.get("/api/goals/cycles").then(res => {
            const list = res.data.map(cycle => ({
                ...cycle,
                status: calculateGoalCycleStatus(cycle),
            }));

            commit("updateList", { list });
        });
    },
    create({ commit }, { payload: { cycle } }) {
        return api.post(`/api/goals/cycles`, cycle).then(res => {
            const data = res.data;
            if (data._id) {
                commit("updateLastCreatedId", { id: data._id });
            }
        });
    },
    get({ commit }, { payload: { id } }) {
        return api.get(`/api/goals/cycles/${id}`).then(res => {
            const data = res.data;
            if (data) {
                commit("updateDetails", { cycle: data });
            }
        });
    },
    update({ commit }, { payload: { id, cycle } }) {
        return api.put(`/api/goals/cycles/${id}`, cycle).then(res => {
            const data = res.data;
            if (data) {
                commit("updateDetails", { cycle: data });
            }
        });
    },
    disable(_, { payload: { id } }) {
        return api.delete(`/api/goals/cycles/${id}`);
    },
};

const mutations = {
    updateList(state, { list }) {
        state.list = [...list];
    },
    updateLastCreatedId(state, { id }) {
        state.lastCreatedId = id;
    },
    updateDetails(state, { cycle }) {
        state.details[cycle._id] = { ...cycle };
    },
};

/**
 * Calculate the status of a give goal cycle by looking at its timeline
 * @param {Object} cycle
 * @return {String} Corresponding status
 */
const calculateGoalCycleStatus = function(cycle) {
    const now = new Date();

    if (new Date(cycle.timeline.plan.date) <= now && now <= new Date(cycle.timeline.end.date)) {
        return "active";
    } else if (now <= new Date(cycle.timeline.plan.date)) {
        return "future";
    } else {
        return "past";
    }
};

/**
 * Sorts goal cycles depending on their status
 *
 * @param {Array} cycles List of cycles
 * @return {Number} -1, 0, 1
 */
const sortCyclesByStatus = function(cycles) {
    // Copy list as sort mutates the Array
    // (cf. https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort)
    //
    const sortedCycles = Array.from(cycles);

    // Comparison matrix
    const compare = {
        active: {},
        future: {},
        past: {},
    };

    compare["active"]["active"] = 0;
    compare["active"]["future"] = -1;
    compare["active"]["past"] = -1;
    compare["future"]["active"] = 1;
    compare["future"]["future"] = 0;
    compare["future"]["past"] = -1;
    compare["past"]["active"] = 1;
    compare["past"]["future"] = 1;
    compare["past"]["past"] = 0;

    // Return sorted list
    return sortedCycles.sort((a, b) => compare[a.status][b.status]);
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};

export const __test__calculateGoalCycleStatus = calculateGoalCycleStatus;
export const __test__sortCyclesByStatus = sortCyclesByStatus;
